import local from "./localStorage";
const PKCE_KEY = "pkce";
const GRANT_KEY = "grant";
const USER_KEY = "user";
// Cross-runtime base64 helpers (browser and Node)
const toBase64 = (input) => {
    // Browser path: use TextEncoder + btoa
    if (typeof globalThis.btoa === "function" &&
        typeof TextEncoder !== "undefined") {
        const utf8Bytes = new TextEncoder().encode(input);
        let binary = "";
        for (let i = 0; i < utf8Bytes.length; i++) {
            binary += String.fromCharCode(utf8Bytes[i]);
        }
        return globalThis.btoa(binary);
    }
    // Node path: use global Buffer without importing
    if (typeof globalThis.Buffer !== "undefined") {
        return globalThis.Buffer.from(input, "utf8").toString("base64");
    }
    throw new Error("Base64 encoding is not supported in this environment");
};
const fromBase64 = (input) => {
    // Browser path: use atob + TextDecoder
    if (typeof globalThis.atob === "function" &&
        typeof TextDecoder !== "undefined") {
        const binary = globalThis.atob(input);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        return new TextDecoder().decode(bytes);
    }
    // Node path: use global Buffer without importing
    if (typeof globalThis.Buffer !== "undefined") {
        return globalThis.Buffer.from(input, "base64").toString("utf8");
    }
    throw new Error("Base64 decoding is not supported in this environment");
};
export class Storage {
    Storage = new local();
    constructor(store) {
        if (store) {
            this.Storage = store;
            return;
        }
    }
    setPKCE(value) {
        const encoded = toBase64(value);
        this.Storage.set(PKCE_KEY, encoded);
    }
    async getPKCE() {
        try {
            const pkce = await this.Storage.get(PKCE_KEY);
            if (pkce) {
                return fromBase64(pkce);
            }
        }
        catch (error) {
            return null;
        }
        return null;
    }
    removePKCE() {
        this.Storage.remove(PKCE_KEY);
    }
    setGrant(resp, multiAccount = false) {
        const { grant_id } = resp;
        this.Storage.set(`${GRANT_KEY}${multiAccount ? "-" + grant_id : ""}`, resp);
    }
    removeGrant(id, multiAccount = false) {
        this.Storage.remove(`${GRANT_KEY}${multiAccount ? "-" + id : ""}`);
    }
    async getGrant(id, multiAccount = false) {
        const grant = await this.Storage.get(`${GRANT_KEY}${multiAccount ? "-" + id : ""}`);
        if (grant) {
            return grant;
        }
        return null;
    }
    // setUserToken is used to store the IDToken in storage
    async setUserToken(token) {
        const userTokens = await this.getUserTokens();
        // If userTokens exist, check if the token is already in the array
        if (userTokens) {
            const userToken = userTokens.find((t) => t.sub === token.sub);
            // If the token is not in the array, add it
            if (!userToken) {
                const newTokens = [...userTokens, token];
                this.Storage.set(USER_KEY, newTokens);
            }
            else {
                // If the token is in the array, update it
                const newUserTokens = userTokens.map((t) => {
                    if (t.sub === token.sub) {
                        t = token;
                    }
                    return t;
                });
                this.Storage.set(USER_KEY, newUserTokens);
            }
        }
        else {
            // If userTokens do not exist, create a new array with the token
            this.Storage.set(USER_KEY, [token]);
        }
    }
    // getUserToken is used to retrieve a specific IDToken from storage
    async getUserToken(id = "") {
        const tokens = await this.Storage.get(USER_KEY);
        if (tokens) {
            if (id === "") {
                // If no id is passed, return the first token
                return tokens[0];
            }
            // Find the token with the matching sub
            const userToken = tokens.find((token) => token.sub === id);
            return userToken;
        }
        return null;
    }
    // getUserTokens is used to retrieve all IDTokens from storage
    async getUserTokens() {
        const tokens = await this.Storage.get(USER_KEY);
        if (tokens) {
            return tokens;
        }
        return null;
    }
    // removeUserTokens is used to remove all IDTokens from storage
    removeUserTokens() {
        this.Storage.remove(USER_KEY);
    }
    // removeUserToken is used to remove a specific IDToken from storage
    async removeUserToken(id) {
        const userTokens = await this.getUserTokens();
        // Filter out the token with the matching sub
        const tokens = userTokens?.filter((token) => token.sub !== id);
        // If there are tokens left, store them
        this.Storage.set(USER_KEY, tokens);
    }
    async clearSession() {
        const tokens = await this.getUserTokens();
        tokens?.forEach((token) => {
            this.removeGrant(token.sub);
        });
        this.removePKCE();
        this.removeUserTokens();
    }
}
